import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/auth/AuthProvider";

/**
 * Wrap protected routes. Behaviour:
 *   - while the initial /auth/me probe is pending, render a small spinner;
 *   - if the probe says no user, bounce to /login (preserving the original
 *     URL in `?next=` so we can return after login);
 *   - if the user must change their password, force them to /account first.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          opacity: 0.6,
        }}
      >
        Loading…
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (user.must_change_password && location.pathname !== "/account") {
    return <Navigate to="/account?forced=1" replace />;
  }

  return <>{children}</>;
}
